import React from 'react';
import { I18n } from 'common/i18n';
import { ForgeIconButton, ForgeIcon, ForgeDateRangePicker, ForgeTextField } from '@tylertech/forge-react';
import { ConditionalConfig, FontStyleTypes } from 'common/visualizations/views/agGridReact/types';
import { ViewColumn } from 'common/types/viewColumn';
import { COLORS } from 'common/authoring_workflow/constants';

import ColorPickerWithLabel from 'common/components/ColorPickerWithLabel';
import { isEmpty, isNull } from 'lodash';
import moment from 'moment';
import OperandFields from './OperandFields';
import OperatorSelector from './OperatorSelector';
import { TIME_FORMATS } from 'common/DataTypeFormatter';
import { getColumnDataTypeInfo } from './helpers';
import { SoQLType } from 'common/types/soql';

const scope = 'shared.visualizations.panes.presentation.fields.ag_updated_column_format.conditional_options';

interface ConditionalMainElementProps {
  config: ConditionalConfig;
  index: number;
  viewColumn: ViewColumn;
  dataType: SoQLType;
  onTextStyleChange: (textStyle: FontStyleTypes) => void;
  onColorChange: (key: string, newColor: string) => void;
  onConditionSelect: (newCondition: string, index?: number) => void;
  onConditionValueChange: (value: string | number, key: string, index?: number) => void;
  onDateValueChange: (from: string, to: string) => void;
}

const ConditionalMainElement: React.FC<ConditionalMainElementProps> = ({
  config,
  index,
  viewColumn,
  dataType,
  onTextStyleChange,
  onColorChange,
  onConditionSelect,
  onConditionValueChange,
  onDateValueChange,
}) => {
  const datePickerLabel = I18n.t('fields.ag_date_range.title', {
    scope: 'shared.visualizations.panes.presentation',
  });
  const backgroundColorLabel = I18n.t('background_label', { scope });
  const textColorLabel = I18n.t('text_label', { scope });

  const { fontStyle, backgroundColor, textColor } = config.style;
  const { function: operator } = config.expression;
  const { start: startValue, end: endValue } = config.expression.arguments;
  const { isNumberRenderType, isBooleanRenderType, isDateRenderType } = getColumnDataTypeInfo(dataType);

  const renderDateRangePicker = () => {
    const datePickerProps = {
      from: startValue ? startValue : null,
      to: endValue ? endValue : null,
      'on-forge-date-range-picker-change': (event: CustomEvent) => {
        const isRange = !isNull(event.detail.from) && !isNull(event.detail.to);
        if (isRange) {
          const start = moment(event.detail.from).format(TIME_FORMATS.date);
          const end = moment(event.detail.to).format(TIME_FORMATS.date);

          // Prevent infinite loop by checking if values have actually changed
          if (start !== startValue || end !== endValue) {
            onDateValueChange(start, end);
          }
        }
      },
      'data-testid': `conditional-date-range-picker-${index}`,
      valueMode: 'iso-string',
    };

    return (
      <>
        <ForgeDateRangePicker {...datePickerProps}>
          <ForgeTextField>
            <label htmlFor={`start-${index}`} slot="label">
              {datePickerLabel}
            </label>
            <input
              type="text"
              id={`start-${index}`}
              autoComplete="off"
              placeholder="mm/dd/yyyy"
              data-testid={`start-date-picker-${index}`}
            />
            <input
              type="text"
              id={`end-${index}`}
              autoComplete="off"
              placeholder="mm/dd/yyyy"
              data-testid={`end-date-picker-${index}`}
            />
          </ForgeTextField>
        </ForgeDateRangePicker>
      </>
    );
  };

  return (
    <div className="conditional-config-content-wrapper">
      <div className="conditional-config-content">
        {!isEmpty(viewColumn.fieldName) && !isDateRenderType && (
          <>
            <OperatorSelector
              index={index}
              condition={operator}
              dataTypeName={dataType}
              onConditionSelect={onConditionSelect}
            />
            {!isBooleanRenderType && (
              <OperandFields
                index={index}
                conditionFormat={config.expression}
                onConditionValueChange={onConditionValueChange}
                isNumberRenderType={isNumberRenderType}
              />
            )}
          </>
        )}
        {isDateRenderType && renderDateRangePicker()}
      </div>
      <div className="cell-style-wrapper">
        <ColorPickerWithLabel
          uniqueId={'conditional-fill' + index}
          keyId={'fill' + index}
          color={backgroundColor}
          onChangeColor={(newColor: string) => onColorChange('backgroundColor', newColor)}
          label={backgroundColorLabel}
          palette={COLORS}
        />
        <ColorPickerWithLabel
          uniqueId={'conditional-text' + index}
          keyId={'text' + index}
          color={textColor}
          onChangeColor={(newColor: string) => onColorChange('textColor', newColor)}
          label={textColorLabel}
          palette={COLORS}
        />
        <ForgeIconButton
          dense
          densityLevel={2}
          toggle
          isOn={!!fontStyle?.isBold}
          aria-label="bold"
          onClick={() => onTextStyleChange(FontStyleTypes.isBold)}
        >
          <ForgeIcon name="format_bold" slot="on" />
          <ForgeIcon name="format_bold" />
        </ForgeIconButton>
        <ForgeIconButton
          dense
          densityLevel={2}
          toggle
          isOn={!!fontStyle?.isItalic}
          aria-label="italic"
          onClick={() => onTextStyleChange(FontStyleTypes.isItalic)}
        >
          <ForgeIcon name="format_italic" slot="on" />
          <ForgeIcon name="format_italic" />
        </ForgeIconButton>
      </div>
    </div>
  );
};

export default ConditionalMainElement;
